import { createAsyncThunk } from "@reduxjs/toolkit";
import AuthServices from "../../Services/AuthServices";
import services from "../../Services/Services";
import {chatLogout, clearMessages} from "../Slices/ChatSocketSlice";
import { debugLog } from "../../Utils/utils";
import {
  buildGuestUser,
  loadGuestSession,
  saveGuestSession,
  takeGuestHandover,
} from "../../Utils/guestSession";
import { keepGuestKey, takeGuestKey, withGuestKeyLock } from "../../Utils/guestKey";
import { getEmbeddedVisitorId } from "../../Utils/embedVisitor";

export const login = createAsyncThunk(
  "auth/login",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthServices.login(userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthServices.register(userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);


export const otpActivate = createAsyncThunk(
  "auth/otpActivate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthServices.otpActivate(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthServices.resendOtp(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, thunkAPI) => {
    try {
      const response = await AuthServices.logout();
      thunkAPI.dispatch(chatLogout());
      thunkAPI.dispatch(clearMessages());
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

const UNIDENTIFIED_GUEST_MESSAGE = "Guest session did not identify a customer";

// Every POST creates a NEW guest customer, so the request must be asked for
// once per shop and shared. Surfaces ask independently — the card, the
// conversation screen and the SDK bridge can all ask in the same tick, and none
// of them can see a request that has started but not answered yet — and two
// requests meant two customers: the widget listened as one and the agent
// answered the other, so the shopper saw nothing.
const guestSessionRequests = new Map();

// The key from the last visit goes with the request, and the one that comes
// back replaces it before anyone else can ask. Both happen inside the lock, so
// a second tab waits and then sends the new key rather than the spent one.
const exchangeGuestKey = (orgId) =>
  withGuestKeyLock(orgId, async () => {
    const guestKey = await takeGuestKey(orgId);
    // The shop's tracker already has a device id for this browser; the guest is
    // created against that one rather than a second id nothing else knows, so
    // the browsing before the conversation belongs to the same visit.
    const visitorId = getEmbeddedVisitorId();
    const response = guestKey
      ? await AuthServices.createGuestSession(orgId, guestKey, visitorId)
      : await AuthServices.createGuestSession(orgId, undefined, visitorId);
    const session = response?.data;
    await keepGuestKey(orgId, session?.guestKey, session?.guestKeyExpiresAt);
    return response;
  });

const askForGuestSession = async (orgId) => {
  const response = await exchangeGuestKey(orgId);
  const session = response?.data;
  const user = buildGuestUser(session);
  if (!session?.chatToken || !user) {
    const error = new Error(UNIDENTIFIED_GUEST_MESSAGE);
    error.rejection = { message: UNIDENTIFIED_GUEST_MESSAGE };
    throw error;
  }
  saveGuestSession(orgId, session.chatToken, user, session.chatId);
  return { token: session.chatToken, user, chatId: session.chatId ?? null };
};

// A visitor who has not signed in but wants to talk to the agent. The backend
// creates a guest customer and returns a token naming them; the widget then
// behaves as signed in for the conversation, and nothing else.
//
// One visit is one guest, however many surfaces ask at once: a session already
// granted for this shop is handed back, whatever is in flight is shared, and
// only a shop with no usable session asks the backend for another.
export const guestSession = createAsyncThunk(
  "auth/guestSession",
  async ({ orgId }, { rejectWithValue }) => {
    const granted = loadGuestSession(orgId);
    if (granted) return granted;

    const key = String(orgId);
    let request = guestSessionRequests.get(key);
    if (!request) {
      // Cleared once answered, so a later visit — or a session that has since
      // expired — asks again rather than reviving a customer who is gone.
      request = askForGuestSession(orgId).finally(() => {
        guestSessionRequests.delete(key);
      });
      guestSessionRequests.set(key, request);
    }

    try {
      return await request;
    } catch (err) {
      return rejectWithValue(err?.rejection || err?.response?.data || { message: err.message });
    }
  }
);

// The conversation follows the person onto the account they just created.
//
// Dispatched with the id of the account that has just signed in or activated,
// and the shop it belongs to, after it has done so: the request then carries
// that account's token, which is how the backend knows who is receiving the
// history. What it hands over was captured by rememberGuestForHandover in the
// same tick that forgot the guest, and is taken out exactly once, for that
// account and that shop only.
//
// Taken out before the call, not after it, so a request that never answers
// leaves nothing behind for the next person on this browser to be handed. The
// cost is that a refused handover is not retried: the history stays on the
// guest record, which is where it already is.
//
// It never rejects. A visitor who has just created an account must not be shown
// a failure about a transfer they never asked for, and nothing in the sign-in
// flow may depend on this succeeding. A failed handover costs the brand the
// history; a failed sign-in costs it the customer.
export const handOverGuestConversation = createAsyncThunk(
  "auth/handOverGuestConversation",
  async ({ userId, orgId } = {}) => {
    const pending = takeGuestHandover(userId, orgId);
    if (!pending) return { handedOver: false, reason: "nothing to hand over" };

    try {
      await AuthServices.mergeGuestSession(pending);
      return { handedOver: true };
    } catch (err) {
      debugLog("Guest handover failed:", err?.response?.status, err?.message);
      return { handedOver: false, reason: "refused" };
    }
  }
);

export const getUserInfo = createAsyncThunk(
  "auth/info",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthServices.getUserInfo();
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const getChatByUserId = createAsyncThunk(
  "auth/getChatByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await AuthServices.getChatByUserId(userId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const getUserChatData = createAsyncThunk(
  "auth/getUserChatData",
  async ({ userId, token }, { rejectWithValue }) => {
    try {
      const response = await services.getUserChatData(userId, token);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthServices.updateUser(data);
      // Backend returns minimal response {status, id} — merge the sent name
      // so the reducer can update state optimistically
      return { ...response.data, name: data.name };
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);